import { useState, useCallback } from 'react';
import { makeStyles, Button, Snackbar } from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import {
  createPlugin,
  fetchApiRef,
  discoveryApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
  useShadowRootElements,
} from '@backstage/plugin-techdocs-react';
import { AddonBlueprint } from '@backstage/plugin-techdocs-react/alpha';

const PAGE_EDIT_LINK_SELECTOR = '[title^="Edit this page"]';

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
}));

const CopyMarkdownToolbarComponent = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [editLink] = useShadowRootElements<HTMLAnchorElement>([
    PAGE_EDIT_LINK_SELECTOR,
  ]);

  const handleCopy = useCallback(async () => {
    const editUrl = editLink?.href;
    if (!editUrl) return;
    try {
      // Convert edit URL to blob URL for github-raw plugin
      const blobUrl = editUrl.replace('/edit/', '/blob/');
      const baseUrl = await discoveryApi.getBaseUrl('github-raw');
      const response = await fetchApi.fetch(
        `${baseUrl}/fetch?url=${encodeURIComponent(blobUrl)}`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const markdown = await response.text();
      await navigator.clipboard.writeText(markdown);
      setOpen(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy markdown:', e);
    }
  }, [editLink, fetchApi, discoveryApi]);

  const showCopy = Boolean(editLink?.href);
  if (!showCopy) return null;

  return (
    <div className={classes.toolbar}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<FileCopyOutlinedIcon />}
        onClick={handleCopy}
      >
        Copy Markdown
      </Button>
      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={() => setOpen(false)}
        message="Markdown copied to clipboard"
      />
    </div>
  );
};

const addonOptions = {
  name: 'CopyMarkdown',
  location: TechDocsAddonLocations.Subheader,
  component: CopyMarkdownToolbarComponent,
};

// New frontend system addon (PAT-1024): `addon:techdocs/copy-markdown`, attached
// to `api:techdocs/addons`, which feeds the NFS TechDocs reader page.
// Registered by the techdocs module (`modules/techdocs`).
export const copyMarkdownAddon = AddonBlueprint.make({
  name: 'copy-markdown',
  params: addonOptions,
});

const copyMarkdownPlugin = createPlugin({
  id: 'techdocs-copy-markdown',
});

// Legacy addon for the entity page's Docs tab (`EntityPage.tsx`), which still
// renders `EntityTechdocsContent` with JSX `<TechDocsAddons>`. Remove it once
// that tab moves to the new frontend system (PAT-1028).
export const CopyMarkdown = copyMarkdownPlugin.provide(
  createTechDocsAddonExtension(addonOptions),
);
